import { Alert, Stack } from '@mantine/core';
import React, { useCallback, useEffect, useState } from 'react';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import { notifications } from '@mantine/notifications';
import StyledCheckbox from '@/components/StyledInputFields/StyledCheckbox.tsx';
import { ExecutionType, StepCreationPreset, StepType, TransformCodeSourceMode } from '@/types/automationFields.ts';
import useRerunStep from '@/hooks/api/runs/rerun/useRerunStep.ts';
import { ExtractEntityRerunOptions, Run } from '@/types/runs.ts';
import { StepRun } from '@/types/stepExecutions.ts';
import useRetrieveRunLatestExecution from '@/hooks/api/runs/useRetrieveRunLatestExecution.ts';
import { ActiveStatuses, ExecutionStatus } from '@/types/statuses.ts';
import { AutomationStep, DecisionControlLogic } from '@/types/automationSteps.ts';
import { ELSE_CLAUSE_ID } from '@/utils/constants.ts';
import CacheBypassNotice from '@/components/CacheBypassNotice.tsx';
import useSubmitInterventionResponse from '@/hooks/api/runs/rerun/useSubmitInterventionResponse.ts';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';

export type RerunStepModalData = {
  childrenToRerun?: 'all' | 'failed';
  extractEntitiesToRerun?: ExtractEntityRerunOptions;
};

type RerunStepModalProps = {
  run: Run;
  stepRun: StepRun;
  step?: AutomationStep;
  rerunData?: RerunStepModalData;
  opened: boolean;
  setOpened: (opened: boolean) => void;
};

const STEPS_THAT_ALLOW_FEEDBACK = [
  StepType.ExtractData,
  StepType.TransformData,
  StepType.Repeat,
  StepType.GetFileFromDirectory,
  StepType.Filter,
  StepType.Validate,
  StepType.Conditional,
];

const stepAllowsFeedback = (stepRun: StepRun, step?: AutomationStep): boolean => {
  if (STEPS_THAT_ALLOW_FEEDBACK.includes(stepRun.step_type)) {
    return true;
  }

  return (
    stepRun.step_type === StepType.Dispatch &&
    step?.step_type === StepType.Dispatch &&
    step.integration_config.action_type === StepCreationPreset.BrowserAutomation
  );
};

const isStrictProvidedCodeControlLogic = (controlLogic?: DecisionControlLogic): boolean =>
  controlLogic?.execution_type === ExecutionType.Code &&
  controlLogic.code_source_mode === TransformCodeSourceMode.ProvidedCode;

const isStrictProvidedCodeRerun = (step?: AutomationStep): boolean => {
  if (step?.step_type === StepType.TransformData) {
    return (
      step.execution_type === ExecutionType.Code && step.code_source_mode === TransformCodeSourceMode.ProvidedCode
    );
  }

  if (step?.step_type === StepType.Validate || step?.step_type === StepType.Repeat) {
    return isStrictProvidedCodeControlLogic(step.control_logic);
  }

  if (step?.step_type === StepType.Conditional) {
    const executableConditions = step.conditions?.filter((condition) => condition.condition_id !== ELSE_CLAUSE_ID) ?? [];
    return (
      executableConditions.length > 0 &&
      executableConditions.every((condition) => isStrictProvidedCodeControlLogic(condition.control_logic))
    );
  }

  return false;
};

const RerunStepModal = ({
  run,
  stepRun,
  step,
  rerunData,
  opened,
  setOpened,
}: RerunStepModalProps) => {
  const [feedback, setFeedback] = useState('');
  const [runDownstream, setRunDownstream] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [responseValues, setResponseValues] = useState<Record<string, string>>({});
  const [interventionError, setInterventionError] = useState<string | null>(null);

  const { data: latestExecution } = useRetrieveRunLatestExecution(run.id);

  const { mutate: rerunStep, isPending, isSuccess } = useRerunStep();
  const {
    mutate: submitInterventionResponse,
    isPending: isSubmittingInterventionResponse,
    isSuccess: interventionResponseSubmitted,
  } = useSubmitInterventionResponse();

  const onClose = useCallback(() => setOpened(false), [setOpened]);

  const strictProvidedCodeRerun = isStrictProvidedCodeRerun(step);
  const interventionResponseContract =
    step?.step_type === StepType.TransformData ? step.intervention_response : undefined;
  const strictProvidedCodeAcceptsRerunFeedback =
    step?.step_type === StepType.TransformData &&
    strictProvidedCodeRerun &&
    !!step.rerun_feedback_binding_name;
  const interventionResponseRequired =
    strictProvidedCodeRerun &&
    !!interventionResponseContract &&
    run.status === ExecutionStatus.AskUser &&
    stepRun.step_execution?.status === ExecutionStatus.AskUser;
  const canResolveIntervention =
    interventionResponseRequired &&
    !!latestExecution &&
    !stepRun.parent_path &&
    stepRun.is_latest &&
    stepRun.execution.id === latestExecution.id &&
    stepRun.step_execution?.status === ExecutionStatus.AskUser;
  const allowsFeedback =
    !interventionResponseRequired &&
    stepAllowsFeedback(stepRun, step) &&
    (!strictProvidedCodeRerun || strictProvidedCodeAcceptsRerunFeedback);
  const interventionResponseErrors = Object.fromEntries(
    (interventionResponseContract?.fields ?? []).map((field) => {
      const value = responseValues[field.key]?.trim() ?? '';
      if (!value) return [field.key, 'This value is required.'];
      if (field.field_type === 'enum' && field.choices.length === 0) {
        return [field.key, 'No allowed choices are configured.'];
      }
      if (field.field_type === 'enum' && !field.choices.includes(value)) {
        return [field.key, 'Select one of the allowed values.'];
      }
      if (
        field.field_type === 'year_month' &&
        (value !== responseValues[field.key] || !/^[1-9]\d{3}-(0[1-9]|1[0-2])$/.test(value))
      ) {
        return [field.key, 'Use YYYY-MM.'];
      }
      return [field.key, undefined];
    }),
  );
  const hasCompleteInterventionResponse =
    !canResolveIntervention ||
    interventionResponseContract?.fields.every((field) => !interventionResponseErrors[field.key]);

  const [ignoreCache, setIgnoreCache] = useState<boolean>(false);

  const runCannotBeRerun =
    run.status === ExecutionStatus.Cancelled || ActiveStatuses.includes(run.status);
  const submitDisabled =
    runCannotBeRerun ||
    isPending ||
    isSubmittingInterventionResponse ||
    submitted ||
    (interventionResponseRequired && !canResolveIntervention) ||
    !hasCompleteInterventionResponse;

  useEffect(() => {
    if (!opened) {
      return;
    }
    // A strict-code feedback binding is a deterministic runtime override, not
    // generation guidance. Never carry stale guidance into that override.
    setFeedback(
      strictProvidedCodeAcceptsRerunFeedback ? '' : latestExecution?.step_feedback?.[stepRun.full_path] || '',
    );
  }, [latestExecution?.step_feedback, opened, stepRun.full_path, strictProvidedCodeAcceptsRerunFeedback]);

  useEffect(() => {
    if (!interventionResponseContract) {
      setResponseValues({});
      return;
    }
    setResponseValues(Object.fromEntries(interventionResponseContract.fields.map((field) => [field.key, ''])));
  }, [interventionResponseContract]);

  useEffect(() => {
    if (isSuccess || interventionResponseSubmitted) {
      onClose();
    }
  }, [interventionResponseSubmitted, isSuccess, onClose]);

  useEffect(() => {
    if (!isPending && !isSubmittingInterventionResponse) {
      setSubmitted(false);
    }
  }, [isPending, isSubmittingInterventionResponse]);

  return (
    <StyledModal opened={opened} onClose={onClose} title={interventionResponseRequired ? 'Human review required' : 'Rerun'}>
      <Stack gap="sm">
        {/*<CustomLoadingOverlay visible={loadingExecution && true} />*/}
        {allowsFeedback && (
          <StyledTextArea
            label={'Feedback'}
            placeholder="Provide feedback that should guide this step when it reruns"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            autoFocus={true}
            data-autofocus
            autosize={true}
            minRows={3}
            // required={true}
          />
        )}
        {strictProvidedCodeRerun && (
          <Alert
            color="blue"
            title={
              interventionResponseRequired
                ? 'Structured input required'
                : strictProvidedCodeAcceptsRerunFeedback
                  ? 'Rerun feedback accepted'
                  : 'Feedback is unavailable for this rerun'
            }
          >
            {interventionResponseRequired
              ? 'Provide the requested structured value below to resolve this paused run. The saved code will receive that value as a typed input. This reruns this step and its downstream dependency closure; it does not rerun upstream ingestion.'
              : strictProvidedCodeAcceptsRerunFeedback
                ? 'This step receives feedback as an explicit runtime input when it is rerun. Provide the correction in the format required by its saved code.'
                : 'This step reruns its saved code unchanged. To change its behavior, edit the step\'s code and run the updated automation.'}
          </Alert>
        )}
        {interventionError && (
          <Alert color="red" title="Unable to continue this run">
            {interventionError}
          </Alert>
        )}
        {canResolveIntervention &&
          interventionResponseContract?.fields.map((field) => {
            const commonProps = {
              label: field.label,
              description: field.description || undefined,
              value: responseValues[field.key] || '',
              error: interventionResponseErrors[field.key],
              required: true,
            };
            if (field.field_type === 'enum') {
              return (
                <StyledSelect
                  key={field.key}
                  {...commonProps}
                  data={field.choices}
                  onChange={(value) =>
                    setResponseValues((current) => ({ ...current, [field.key]: value ?? '' }))
                  }
                />
              );
            }
            return (
              <StyledTextInput
                key={field.key}
                {...commonProps}
                placeholder={field.field_type === 'year_month' ? 'YYYY-MM' : undefined}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  setResponseValues((current) => ({ ...current, [field.key]: value }));
                }}
              />
            );
          })}
        {interventionResponseRequired && !canResolveIntervention && (
          <Alert color="blue" title="Checking response eligibility">
            This paused step must be verified before it can be resolved and rerun.
          </Alert>
        )}
        {!canResolveIntervention && (
          <>
            <StyledCheckbox
              size={'xs'}
              checked={runDownstream}
              onChange={(event) => setRunDownstream(event.currentTarget.checked)}
              label="Rerun downstream steps"
            />
            <StyledCheckbox
              size={'xs'}
              label={'Force rerun all steps (ignore cached data)'}
              checked={ignoreCache}
              onChange={(event) => setIgnoreCache(event.currentTarget.checked)}
            />
            <CacheBypassNotice enabled={ignoreCache} />
          </>
        )}
      </Stack>
      <ModalConfirmationButtons
        onClose={onClose}
        onConfirm={() => {
          if (submitDisabled) {
            return;
          }

          setSubmitted(true);

          notifications.show({
            id: canResolveIntervention ? undefined : `rerun-${run.id}`,
            title: canResolveIntervention ? 'Submitting review' : 'Rerunning',
            message: canResolveIntervention ? 'Submitting your response and continuing the run...' : 'Rerunning, please wait...',
            color: 'green',
          });

          if (canResolveIntervention && latestExecution) {
            setInterventionError(null);
            submitInterventionResponse({
              runId: run.id,
              stepRunId: stepRun.id,
              expectedExecutionId: latestExecution.id,
              values: responseValues,
            }, {
              onError: (error) => {
                setSubmitted(false);
                setInterventionError(getInterventionErrorMessage(error));
              },
            });
            return;
          }

          rerunStep({
            runId: run.id,
            stepPath: stepRun.full_path,
            rerunContext: stepRun.rerun_eligibility
              ? {
                  expected_base_execution_id: stepRun.rerun_eligibility.expected_base_execution_id,
                  source_step_run_id: stepRun.rerun_eligibility.source_step_run_id,
                  parent_step_run_ids: stepRun.rerun_eligibility.parent_step_run_ids,
                }
              : { expected_base_execution_id: run.latest_execution?.id ?? null },
            ignoreCache: ignoreCache,
            feedback: makeStepFeedback(
              latestExecution?.step_feedback || {},
              stepRun.full_path,
              strictProvidedCodeRerun && !strictProvidedCodeAcceptsRerunFeedback ? '' : feedback,
            ),
            runDownstream: runDownstream,
            childrenToRerun: rerunData?.childrenToRerun,
            extractEntitiesToRerun: rerunData?.extractEntitiesToRerun,
          });
        }}
        confirmText={interventionResponseRequired ? 'Submit and continue run' : 'Rerun'}
        confirmDisabled={submitDisabled}
        closeOnConfirm={false}
        confirmLoading={isPending || isSubmittingInterventionResponse || submitted}
      />
    </StyledModal>
  );
};

const getInterventionErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = error.response;
    if (typeof response === 'object' && response !== null && 'data' in response) {
      const data = response.data;
      if (typeof data === 'object' && data !== null && 'detail' in data && typeof data.detail === 'string') {
        return data.detail;
      }
    }
  }
  return 'The review response could not be submitted. Refresh the run and try again.';
};

const makeStepFeedback = (
  existingFeedback: Record<string, string>,
  stepPath: string,
  feedback: string,
): Record<string, string> => {
  const nextFeedback = { ...existingFeedback };
  if (feedback.trim()) {
    nextFeedback[stepPath] = feedback;
  } else {
    delete nextFeedback[stepPath];
  }
  return nextFeedback;
};

export default RerunStepModal;
